import type { Notification } from '../../../../lib/flotiq-client/types/external-api-response';

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useLocalStorageState from 'use-local-storage-state';

import PopoverComponent from '../../../../components/Popover/Popover';
import { TopbarButton } from '../../../../components/Topbar/buttons/base/TopbarButton';

import { BellIcon } from '../../../../images/shapes';

import './notifications.css';

import type { UserLocalStorage } from '../../../../types/localStorage';
import type { OnNotificationsRead } from './NotificationsContent';

import moment from 'moment';

import { getEnv } from '../../../../lib/env';
import { getTestProps } from '../../../../lib/helpers';

import { PopoverPanelPlacement } from '../../../../components/Popover/classes';

import NotificationsContent from './NotificationsContent';

const NotificationsTopbarButton = ({
  notifications,
  notificationsLoading,
  onNotificationsRead,
  testId,
}: {
  notifications: Notification[];
  notificationsLoading: boolean;
  onNotificationsRead?: OnNotificationsRead;
  testId?: string;
}) => {
  const { t } = useTranslation();
  const [user] = useLocalStorageState<UserLocalStorage>('cms.user');

  const notificationsEnabled =
    getEnv('VITE_NOTIFICATIONS_URL') &&
    getEnv('VITE_NOTIFICATIONS_X_AUTH_TOKEN');

  const isNewNotification = useMemo(() => {
    const lastReadNotificationDate = user?.data?.config?.lastNotificationDate;
    return !lastReadNotificationDate && notifications?.length > 0
      ? true
      : notifications?.filter(({ internal }) =>
          moment(internal.createdAt).isAfter(moment(lastReadNotificationDate)),
        ).length > 0;
  }, [notifications, user?.data?.config?.lastNotificationDate]);

  if (!notificationsEnabled) {
    return null;
  }

  return (
    <PopoverComponent
      popoverButton={
        <TopbarButton
          as="div"
          iconPosition="start"
          iconImage={
            <div className="relative">
              <BellIcon />
              {isNewNotification && (
                <div
                  className="bg-red h-1 w-1 rounded-full absolute top-1 right-0 animate-heartbeat
                            outline-1 outline-white dark:outline-slate-950"
                  {...getTestProps(testId, 'bell-new-indicator')}
                />
              )}
            </div>
          }
          buttonColor="transparent"
          additionalClasses="h-11 w-11 xl:h-8 xl:w-8"
          additionalIconClasses="w-4 min-w-4"
          testIdSuffix="open-notifications"
        />
      }
      content={
        <>
          <div
            className="bg-slate-50 dark:bg-slate-950 px-4 py-3 font-bold
                    border-b border-slate-300 dark:border-slate-800 rounded-t-md"
          >
            {t('Global.Notifications')}
          </div>
          <div className="px-4">
            <NotificationsContent
              notifications={notifications}
              notificationsLoading={notificationsLoading}
              onNotificationsRead={onNotificationsRead}
              {...getTestProps(testId, 'items', 'testId')}
            />
          </div>
        </>
      }
      popoverPhonePanelPlacement={PopoverPanelPlacement.BottomRight}
      popoverPanelPlacement={PopoverPanelPlacement.BottomRight}
      additionalPopoverPanelClasses="max-w-[calc(100vw-2rem)] w-sm border-blue dark:border-blue mt-3 text-slate-900"
      {...getTestProps(testId, '', 'testId')}
    />
  );
};
export default NotificationsTopbarButton;
